import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Clock, Calendar, User, Quote } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import CTACard from "@/components/blog/CTACard";
import BlogImage from "@/components/blog/BlogImage";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import { getPostBySlug } from "@/data/blogPosts";
const MeditacaoMudouMinhaVida = () => {
  const post = getPostBySlug("como-a-meditacao-diaria-mudou-minha-vida") || {
    metaTitle: "Como a Meditação Diária Mudou Minha Vida: Depoimento de Empresário | HabitFrog",
    metaDescription: "Depoimento real de um empresário que fatura R$ 5 milhões/mês sobre como a meditação transformou sua liderança.",
    category: "Bem-estar",
    author: "Ricardo Mendes",
    date: "20 Dez 2025",
    readTime: "25 min de leitura",
    slug: "como-a-meditacao-diaria-mudou-minha-vida",
    title: "Como a Meditação Diária Mudou Minha Vida"
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <meta property="og:title" content={post.metaTitle} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.metaTitle} />
        <meta name="twitter:description" content={post.metaDescription} />
        <link rel="canonical" href={`https://habitfrog.com/posts/${post.slug}`} />
      </Helmet>

      <Header />
      <ScrollToTopButton />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <nav className="mb-8" aria-label="Breadcrumb">
            <Link to="/home" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Voltar ao início
            </Link>
          </nav>

          <header className="max-w-4xl mx-auto mb-12">
            <span className="inline-block bg-coral/20 text-coral px-3 py-1 rounded-full text-sm font-semibold mb-4">
              {post.category}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6 leading-tight">
              {post.title}
            </h1>
            <p className="text-xl text-muted-foreground mb-6">
              Um depoimento real de transformação: de empresário explosivo a líder consciente
            </p>
            <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{post.readTime}</span>
              </div>
            </div>
          </header>

          <article className="max-w-4xl mx-auto prose prose-lg max-w-none">
            {/* Introdução */}
            <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-secondary/20 p-8 rounded-2xl border mb-12">
              <div className="flex items-start gap-4">
                <Quote className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                <div>
                  <p className="text-xl text-foreground italic leading-relaxed mb-4">
                    "Eu era o tipo de empresário que fazia os funcionários tremerem quando eu entrava na sala. Minha empresa faturava R$ 5 milhões por mês, mas eu estava perdendo tudo que realmente importava: minha saúde, meu casamento, o respeito da minha equipe. Hoje, depois de dois anos meditando diariamente, meu faturamento dobrou – mas o mais importante é que finalmente tenho paz."
                  </p>
                  <p className="text-muted-foreground font-semibold">— Ricardo Mendes, CEO da TechFlow Solutions</p>
                </div>
              </div>
            </div>

            <div className="mb-12">
              <p className="text-xl text-muted-foreground leading-relaxed">
                Este não é um artigo sobre técnicas de meditação. É uma história crua e honesta sobre como um empresário de sucesso – pelo menos na superfície – estava destruindo tudo ao seu redor por não conseguir controlar suas próprias emoções. É a jornada de um homem que descobriu, aos 43 anos, que todo o dinheiro do mundo não compra paz interior, e que 10 minutos diários de silêncio podem transformar mais do que qualquer MBA ou consultoria empresarial.
              </p>
            </div>

            <BlogImage 
              src="/images/blog/empresario-maduro-pensativo.jpg" 
              alt="Empresário maduro pensativo em ambiente sofisticado, representando Ricardo Mendes CEO refletindo sobre sua jornada de transformação através da meditação" 
              className="h-64 md:h-80 mb-12"
              priority
            />

            {/* Parte 1: O Empresário que Todos Temiam */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6">Parte 1: O Empresário que Todos Temiam</h2>
              
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4">O "Sucesso" que Custava Caro</h3>
                  <p className="text-foreground/80 mb-4">
                    Em 2021, eu era o que muitos chamariam de empresário bem-sucedido. Minha empresa de tecnologia, a TechFlow Solutions, tinha 127 funcionários e faturava consistentemente R$ 5 milhões por mês. Tínhamos clientes de peso, escritórios elegantes em São Paulo e reconhecimento no mercado.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    Mas por trás dessa fachada de sucesso, havia uma realidade que eu tentava esconder: eu era um tirano. Um líder pelo medo, não pelo respeito. Um homem que confundia agressividade com assertividade, explosões de raiva com "paixão pelo negócio".
                  </p>
                  <p className="text-foreground/80">
                    Meus funcionários tinham um grupo secreto no WhatsApp chamado "Alerta Tempestade", onde avisavam uns aos outros quando eu chegava de mau humor. E isso era quase todos os dias.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4">Os Sinais que Eu Ignorava</h3>
                  <p className="text-foreground/80 mb-4">
                    A rotatividade de funcionários era de 40% ao ano. Perdíamos talentos incríveis porque ninguém aguentava trabalhar comigo por muito tempo. Os melhores desenvolvedores preferiam ganhar menos em outras empresas do que lidar com minhas reuniões explosivas.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    Fornecedores importantes rescindiam contratos citando "dificuldades de relacionamento". Parceiros estratégicos recusavam reuniões comigo, preferindo negociar com meus diretores. Eu sabia que tinha um problema, mas me convencia de que era "apenas intensidade".
                  </p>
                  <p className="text-foreground/80">
                    <strong>"Quem quer resultados precisa cobrar com firmeza"</strong>, eu dizia. Mas no fundo, eu sabia que estava perdendo o controle.
                  </p>
                </div>
              </div>

              <div className="bg-coral/10 p-6 rounded-xl border-l-4 border-coral mb-8">
                <h3 className="font-bold text-foreground mb-2">O Momento do Espelho</h3>
                <p className="text-foreground/80">
                  O ponto de virada veio quando minha filha de 15 anos, Laura, me disse algo que nunca vou esquecer: "Pai, quando você chega em casa, eu vou pro meu quarto porque fico com medo de fazer alguma coisa errada e você gritar comigo. Igual você faz com todo mundo." Ela estava tremendo quando disse isso. Minha própria filha tinha medo de mim. Naquele momento, percebi que eu não era um líder forte – eu era apenas um homem assustado que assustava os outros.
                </p>
              </div>
            </section>

            {/* Parte 2: A Resistência Inicial */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6">Parte 2: Quando Ouvi Falar em Meditação pela Primeira Vez</h2>

              <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                <BlogImage 
                  src="/images/blog/meditacao-homem-escritorio.jpg" 
                  alt="Homem de negócios praticando meditação em ambiente de escritório, representando o primeiro contato do empresário com mindfulness" 
                  className="h-64"
                />
                <div>
                  <p className="text-foreground/80 mb-4">
                    Meu sócio, Eduardo, praticava meditação há anos. Eu sempre zoava ele, chamava de "esotérico", "hippie executivo". Para mim, meditação era coisa de gente que não tinha coragem de enfrentar os problemas de frente.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    Mas depois daquela conversa com minha filha, procurei Eduardo. Ele não julgou, não disse "eu avisei". Apenas me convidou para experimentar meditar com ele por uma semana, 10 minutos por dia.
                  </p>
                  <p className="text-foreground/80">
                    "Você consegue ficar 10 minutos em silêncio?", ele perguntou. Eu ri. "Claro que consigo." Eu estava completamente errado.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-card p-6 rounded-xl border">
                  <div className="text-4xl mb-4">😤</div>
                  <h3 className="font-semibold text-foreground mb-3">Os Primeiros Dias</h3>
                  <p className="text-sm text-muted-foreground">
                    Foram terríveis. Minha mente não parava. Eu ficava pensando em e-mails, reuniões, problemas. 10 minutos pareciam 10 horas. Eu queria desistir após o segundo dia.
                  </p>
                </div>
                <div className="bg-card p-6 rounded-xl border">
                  <div className="text-4xl mb-4">🤯</div>
                  <h3 className="font-semibold text-foreground mb-3">A Descoberta Chocante</h3>
                  <p className="text-sm text-muted-foreground">
                    Percebi que não conseguia ficar sozinho com meus próprios pensamentos. Toda minha vida era uma fuga – do trabalho às notícias, do celular às reuniões. Eu tinha pavor do silêncio.
                  </p>
                </div>
                <div className="bg-card p-6 rounded-xl border">
                  <div className="text-4xl mb-4">🔄</div>
                  <h3 className="font-semibold text-foreground mb-3">O Compromisso</h3>
                  <p className="text-sm text-muted-foreground">
                    Eduardo me desafiou: "Uma semana, apenas uma semana. Se não funcionar, você nunca mais precisa fazer." Aceitei. Afinal, eu era um empresário – sabia honrar compromissos.
                  </p>
                </div>
              </div>
            </section>

            <div className="my-16"><CTACard variant="primary" /></div>

            {/* Parte 3: Os Primeiros 30 Dias */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6">Parte 3: Os Primeiros 30 Dias – Caos, Resistência e um Lampejo de Paz</h2>

              <p className="text-foreground/80 mb-8">
                Decidi fazer um experimento sério: 30 dias de meditação diária, sem falhar. Se eu conseguia manter disciplina em acordar às 5h da manhã para malhar, por que não conseguiria para meditar?
              </p>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4">Semana 1-2: O Inferno Mental</h3>
                  <p className="text-foreground/80 mb-4">
                    Eu sentava para meditar e minha mente explodia em pensamentos. Listas de tarefas, conversas imaginárias, memórias aleatórias, julgamentos sobre estar "perdendo tempo". Era como tentar dormir com a TV ligada no volume máximo.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    Eduardo me ensinou que isso era normal, que o objetivo não era "parar de pensar", mas sim observar os pensamentos sem se prender a eles. "Imagine que seus pensamentos são carros passando na rua. Você não precisa entrar em cada um."
                  </p>
                  <p className="text-foreground/80">
                    Essa metáfora foi um divisor de águas. Comecei a perceber que <strong>eu não era meus pensamentos</strong>. Pela primeira vez na vida, experimentei um milésimo de segundo de separação entre um pensamento raivoso e minha reação a ele.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4">Semana 3-4: Os Primeiros Sinais</h3>
                  <p className="text-foreground/80 mb-4">
                    Algo começou a mudar sutilmente. Em uma reunião tensa com um cliente insatisfeito, percebi minha respiração acelerando e meus ombros subindo. Em vez de explodir como sempre fazia, respirei fundo – conscientemente – e respondi com calma.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    Meu diretor financeiro, Carlos, me perguntou depois: "Você tomou algum remédio? Está tudo bem?" Ele genuinamente achou que eu estava doente porque não tinha gritado.
                  </p>
                  <p className="text-foreground/80">
                    Esse foi o primeiro momento em que percebi que a meditação não era sobre ficar zen sentado em silêncio – era sobre <strong>criar espaço entre estímulo e resposta</strong> nas situações reais da vida.
                  </p>
                </div>
              </div>

              <BlogImage 
                src="/images/blog/yoga-meditacao-mulher.jpg" 
                alt="Pessoa praticando meditação ao amanhecer com postura tranquila, representando a consistência da prática diária de mindfulness" 
                className="h-64 md:h-80 mb-8"
              />
            </section>

            {/* Parte 4: Seis Meses Depois */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6">Parte 4: Seis Meses Depois – A Empresa Começou a Mudar Porque Eu Mudei</h2>

              <div className="bg-gradient-to-r from-primary/10 to-accent/10 p-8 rounded-2xl border mb-8">
                <p className="text-lg text-foreground/90 italic">
                  "Depois de seis meses meditando todos os dias, percebi que a TechFlow não tinha um problema de cultura – tinha um problema de liderança. E esse problema era eu. Quando comecei a mudar, a empresa inteira começou a respirar."
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-card p-6 rounded-xl border">
                  <div className="text-4xl mb-4">📉</div>
                  <h3 className="font-semibold text-foreground mb-3">Rotatividade Caiu 60%</h3>
                  <p className="text-sm text-muted-foreground">
                    A taxa de turnover, que era de 40% ao ano, caiu para 16%. Funcionários pararam de sair. Alguns que tinham ido embora pediram para voltar.
                  </p>
                </div>
                <div className="bg-card p-6 rounded-xl border">
                  <div className="text-4xl mb-4">🤝</div>
                  <h3 className="font-semibold text-foreground mb-3">Fornecedores Voltaram</h3>
                  <p className="text-sm text-muted-foreground">
                    Três fornecedores que tinham rescindido contrato voltaram a fazer negócio conosco. Um deles me disse: "Não sei o que aconteceu com você, mas você virou outra pessoa."
                  </p>
                </div>
                <div className="bg-card p-6 rounded-xl border">
                  <div className="text-4xl mb-4">💰</div>
                  <h3 className="font-semibold text-foreground mb-3">Faturamento Subiu 35%</h3>
                  <p className="text-sm text-muted-foreground">
                    Sem mudar estratégia, sem contratar consultoria, sem novo produto. Apenas melhorando o ambiente, a criatividade e o engajamento da equipe floresceram.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4">A Lição Mais Importante</h3>
                  <p className="text-foreground/80 mb-4">
                    A maior descoberta não foi sobre meditação – foi sobre liderança. Eu achava que tinha que ser duro para ser respeitado. Que medo e respeito eram a mesma coisa. Que mostrar vulnerabilidade era fraqueza.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    Estava completamente errado. Quando comecei a admitir meus erros, pedir desculpas genuínas e ouvir de verdade, minha equipe começou a confiar em mim. E confiança gera muito mais resultados do que medo.
                  </p>
                  <p className="text-foreground/80">
                    <strong>A meditação não me ensinou a controlar minha raiva. Me ensinou a não ter tanta raiva para controlar.</strong>
                  </p>
                </div>
                <BlogImage 
                  src="/images/blog/equipe-reuniao.jpg" 
                  alt="Reunião de equipe colaborativa com ambiente positivo e funcionários engajados após transformação da liderança" 
                  className="h-64"
                />
              </div>
            </section>

            {/* Parte 5: Minha Família de Volta */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6">Parte 5: Minha Família de Volta</h2>

              <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                <BlogImage 
                  src="/images/blog/familia-feliz.jpg" 
                  alt="Família reunida em momento de conexão genuína e felicidade após transformação através da meditação" 
                  className="h-64"
                />
                <div>
                  <p className="text-foreground/80 mb-4">
                    A mudança mais significativa não foi nos números da empresa – foi em casa. Minha esposa, Márcia, disse que estava prestes a pedir o divórcio quando eu comecei a meditar. Ela não aguentava mais viver com uma "bomba-relógio".
                  </p>
                  <p className="text-foreground/80 mb-4">
                    Hoje, temos rituais sagrados: café da manhã em família sem celular, caminhadas no fim de semana, conversas verdadeiras sobre sentimentos. Coisas que pareciam impossíveis dois anos atrás.
                  </p>
                  <p className="text-foreground/80">
                    E Laura, minha filha que tinha medo de mim? Agora medita comigo três vezes por semana. Virou nosso momento especial. Ela diz que tenho "olhos diferentes" – mais calmos, mais presentes.
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-golden/20 to-golden/5 p-8 rounded-2xl border border-golden/30 mb-8">
                <div className="flex items-start gap-4">
                  <Quote className="w-8 h-8 text-golden flex-shrink-0 mt-1" />
                  <p className="text-lg text-foreground italic">
                    "Pai, você sabe o que mais mudou? Antes, quando você chegava em casa, a gente se escondia. Agora, a gente corre pra porta pra te receber. É a mesma porta, mas você é uma pessoa diferente passando por ela."
                    <span className="block mt-2 text-muted-foreground not-italic">— Laura, minha filha de 17 anos</span>
                  </p>
                </div>
              </div>
            </section>

            {/* Parte 6: O Que a Meditação Realmente É */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6">Parte 6: O Que a Meditação Realmente É (E O Que Não É)</h2>

              <p className="text-foreground/80 mb-8">
                Depois de dois anos praticando diariamente, posso dizer com confiança o que a meditação significa para mim – e derrubar alguns mitos que eu mesmo tinha.
              </p>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div className="bg-coral/10 p-6 rounded-xl border-l-4 border-coral">
                  <h3 className="font-bold text-foreground mb-4">❌ O Que NÃO É</h3>
                  <ul className="space-y-3 text-foreground/80">
                    <li>• <strong>Não é esvaziar a mente:</strong> pensamentos vão aparecer, sempre. O objetivo é mudar sua relação com eles.</li>
                    <li>• <strong>Não é relaxamento:</strong> às vezes você senta para meditar e fica mais agitado. Isso também faz parte.</li>
                    <li>• <strong>Não é religião:</strong> você não precisa acreditar em nada específico. É treino mental.</li>
                    <li>• <strong>Não é perda de tempo:</strong> os 10 minutos que você "perde" meditando, você ganha em decisões melhores o dia todo.</li>
                  </ul>
                </div>
                <div className="bg-primary/10 p-6 rounded-xl border-l-4 border-primary">
                  <h3 className="font-bold text-foreground mb-4">✅ O Que É</h3>
                  <ul className="space-y-3 text-foreground/80">
                    <li>• <strong>É treino de atenção:</strong> você aprende a focar no que escolhe, não no que seu cérebro decide.</li>
                    <li>• <strong>É autoconhecimento:</strong> você descobre padrões de pensamento que nunca tinha percebido.</li>
                    <li>• <strong>É criação de espaço:</strong> entre o que acontece e como você reage, surge um momento de escolha.</li>
                    <li>• <strong>É hábito:</strong> como exercício físico para a mente. Precisa de constância para dar resultados.</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Parte 7: Como Comecei (Guia Prático) */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6">Parte 7: Como Você Pode Começar Hoje</h2>

              <p className="text-foreground/80 mb-8">
                Se eu consegui – um empresário cético, impaciente e viciado em produtividade – qualquer pessoa consegue. Aqui está exatamente o que eu fiz:
              </p>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-card p-6 rounded-xl border">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold mb-4">1</div>
                  <h3 className="font-semibold text-foreground mb-3">Escolha um Horário Fixo</h3>
                  <p className="text-sm text-muted-foreground">
                    Eu medito às 6h da manhã, antes de olhar qualquer tela. Associar a um momento específico do dia torna mais fácil criar o hábito.
                  </p>
                </div>
                <div className="bg-card p-6 rounded-xl border">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold mb-4">2</div>
                  <h3 className="font-semibold text-foreground mb-3">Comece Ridiculamente Pequeno</h3>
                  <p className="text-sm text-muted-foreground">
                    Não comece com 20 minutos. Comece com 2. Sim, dois minutos. Quando isso virar rotina, aumente para 5, depois 10.
                  </p>
                </div>
                <div className="bg-card p-6 rounded-xl border">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold mb-4">3</div>
                  <h3 className="font-semibold text-foreground mb-3">Use um App ou Guia</h3>
                  <p className="text-sm text-muted-foreground">
                    No início, ter uma voz guiando ajuda muito. Use apps como Headspace, Calm, ou vídeos no YouTube. Com o tempo, você consegue meditar em silêncio.
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-card to-accent/10 p-8 rounded-2xl border mb-8">
                <h3 className="text-xl font-bold text-foreground mb-4">A Técnica Básica que Eu Uso</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <ol className="space-y-3 text-foreground/80">
                      <li><strong>1.</strong> Sente confortavelmente, coluna reta</li>
                      <li><strong>2.</strong> Feche os olhos</li>
                      <li><strong>3.</strong> Foque na sua respiração</li>
                      <li><strong>4.</strong> Quando perceber que se distraiu, volte para a respiração gentilmente</li>
                    </ol>
                  </div>
                  <div>
                    <p className="text-foreground/80 mb-4">
                      O passo 4 é o mais importante. Você VAI se distrair, dezenas de vezes. Cada vez que você percebe e volta, está fortalecendo seu "músculo" de atenção.
                    </p>
                    <p className="text-foreground/80">
                      <strong>Não existe meditação "ruim". Existe apenas prática.</strong>
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <div className="my-16"><CTACard variant="secondary" /></div>

            {/* Parte 8: Dois Anos Depois */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6">Parte 8: Dois Anos Depois – O Que Mudou de Verdade</h2>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4">Nos Negócios</h3>
                  <ul className="space-y-3 text-foreground/80">
                    <li>• Faturamento: de R$ 5M para R$ 10.5M mensais</li>
                    <li>• Funcionários: de 127 para 203</li>
                    <li>• Rotatividade: de 40% para 12% ao ano</li>
                    <li>• Satisfação da equipe (pesquisa anônima): de 54% para 89%</li>
                    <li>• Novos contratos vindos de indicação: aumentaram 340%</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4">Na Vida Pessoal</h3>
                  <ul className="space-y-3 text-foreground/80">
                    <li>• Casamento: mais forte do que nunca</li>
                    <li>• Relacionamento com filhos: transformado completamente</li>
                    <li>• Pressão arterial: normalizada sem medicação</li>
                    <li>• Qualidade do sono: de 4-5 horas para 7-8 horas</li>
                    <li>• Amigos verdadeiros: de 2 para vários que confiam em mim</li>
                  </ul>
                </div>
              </div>

              <BlogImage 
                src="/images/blog/meditacao-escritorio.jpg" 
                alt="Empresário em momento de reflexão contemplativa com vista de cidade ao fundo representando paz interior" 
                className="h-64 md:h-80 mb-8"
              />
            </section>

            {/* Conclusão */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6">Conclusão: A Verdadeira Riqueza</h2>
              
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <p className="text-foreground/80 mb-4">
                    Se você me perguntasse dois anos atrás o que era sucesso, eu diria: "faturamento, reconhecimento, poder". Hoje minha resposta é diferente: <strong>sucesso é ter paz interior independente das circunstâncias externas</strong>.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    Isso não significa que não me importo com resultados – muito pelo contrário. Mas resultados construídos sobre ansiedade, medo e agressividade são frágeis. Resultados construídos sobre clareza, calma e conexão genuína são sustentáveis.
                  </p>
                  <p className="text-foreground/80">
                    A meditação não mudou minha vida porque me deu superpoderes. Mudou porque me mostrou quem eu realmente era – e me deu ferramentas para me tornar quem eu queria ser.
                  </p>
                </div>
                <BlogImage 
                  src="/images/blog/quarto-noite-sono.jpg" 
                  alt="Nascer do sol representando novo começo, transformação e esperança através da prática de meditação" 
                  className="h-64"
                />
              </div>

              <div className="bg-gradient-to-r from-primary/20 via-accent/20 to-secondary/20 p-8 rounded-2xl border mb-8">
                <p className="text-xl text-foreground text-center">
                  <strong>Se eu pudesse voltar no tempo e dar um conselho ao Ricardo de dois anos atrás, seria simples:</strong>
                </p>
                <p className="text-2xl font-bold text-primary text-center mt-4">
                  "Para. Respira. Tudo que você busca lá fora está esperando por você aqui dentro. Você só precisa de 10 minutos de silêncio por dia para encontrar."
                </p>
              </div>

              <p className="text-xl font-semibold text-primary text-center py-4">
                Não espere chegar ao fundo do poço como eu cheguei. Comece hoje. Dois minutos. É o suficiente para iniciar a transformação mais importante da sua vida.
              </p>
            </section>

            <CTACard variant="primary" />
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MeditacaoMudouMinhaVida;
